import React from "react";
import "./Footer.css";

const LOG_TAIL_LINES = 1200;

const getToneByKey = (key) => {
  const k = key.toLowerCase();
  if (k.includes("button")) return "tone-action";
  if (k.includes("user")) return "tone-user";
  if (k.includes("time")) return "tone-time";
  if (k.includes("ip")) return "tone-ip";
  if (k.includes("ua")) return "tone-ua";
  return "tone-default";
};

const highlightText = (text, query) => {
  const term = query.trim();
  if (!term) return text;

  const lowerText = text.toLowerCase();
  const lowerTerm = term.toLowerCase();
  if (!lowerText.includes(lowerTerm)) return text;

  const pieces = [];
  let start = 0;
  let matchIndex = lowerText.indexOf(lowerTerm);

  while (matchIndex !== -1) {
    if (matchIndex > start) {
      pieces.push(text.slice(start, matchIndex));
    }

    pieces.push(
      <mark className="log-highlight" key={`${matchIndex}-${start}`}>
        {text.slice(matchIndex, matchIndex + term.length)}
      </mark>,
    );

    start = matchIndex + term.length;
    matchIndex = lowerText.indexOf(lowerTerm, start);
  }

  if (start < text.length) {
    pieces.push(text.slice(start));
  }

  return pieces;
};

const Footer = () => {
  const year = new Date().getFullYear();
  const [showLog, setShowLog] = React.useState(false);
  const [logText, setLogText] = React.useState("");
  const [serverEntries, setServerEntries] = React.useState([]);
  const [showServerLog, setShowServerLog] = React.useState(false);
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const [modalError, setModalError] = React.useState("");
  const [modalLoading, setModalLoading] = React.useState(false);
  const [storedKey, setStoredKey] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchMatches, setSearchMatches] = React.useState([]);
  const [activeMatchIndex, setActiveMatchIndex] = React.useState(0);
  const [searchMeta, setSearchMeta] = React.useState({ totalMatches: 0, truncated: false });
  const logLineRefs = React.useRef([]);

  const parseLogText = React.useCallback((text) => {
    const lines = (text || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .reverse();

    return lines.length ? lines : ["No log entries yet."];
  }, []);

  const fetchServerLog = React.useCallback(
    async (key) => {
      const res = await fetch(
        `/api/log/view?key=${encodeURIComponent(key)}&tailLines=${LOG_TAIL_LINES}`,
      );
      if (!res.ok) throw new Error("Failed to load log");
      const text = await res.text();
      const entries = parseLogText(text);
      setServerEntries(entries);
    },
    [parseLogText],
  );

  // Auto-refresh log while viewer is open
  React.useEffect(() => {
    if (!showServerLog || !storedKey) return;
    const interval = setInterval(async () => {
      try {
        await fetchServerLog(storedKey);
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [showServerLog, storedKey, fetchServerLog]);

  React.useEffect(() => {
    if (!showServerLog || !storedKey) return;

    const term = searchTerm.trim();
    if (!term) {
      setSearchMatches([]);
      setSearchMeta({ totalMatches: 0, truncated: false });
      setActiveMatchIndex(0);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(
          `/api/log/search?key=${encodeURIComponent(storedKey)}&q=${encodeURIComponent(term)}&limit=2000`,
        );
        if (!res.ok) throw new Error("Search failed");

        const data = await res.json();
        if (cancelled) return;

        const ordered = (data.matches || []).map((item) => item.text).reverse();
        setSearchMatches(ordered);
        setSearchMeta({
          totalMatches: data.totalMatches || ordered.length,
          truncated: !!data.truncated,
        });
        setActiveMatchIndex(0);
      } catch {
        if (cancelled) return;
        setSearchMatches([]);
        setSearchMeta({ totalMatches: 0, truncated: false });
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showServerLog, storedKey, searchTerm]);

  React.useEffect(() => {
    if (!searchTerm.trim() || searchMatches.length === 0) return;
    const activeNode = logLineRefs.current[activeMatchIndex];
    if (activeNode) {
      activeNode.scrollIntoView({ block: "nearest" });
    }
  }, [activeMatchIndex, searchMatches, searchTerm]);

  // Handle text changes in the log textarea
  const handleLogChange = (e) => setLogText(e.target.value);

  const handleCloseLog = () => {
    localStorage.setItem("loginLog", logText);
    setShowLog(false);
  };

  const handleRightsClick = () => {
    let log = localStorage.getItem("loginLog") || "No log entries.";
    if (log !== "No log entries." && !log.endsWith("\n\n")) {
      log = log.replace(/\n/g, "\n\n").replace(/\n\n\n+/g, "\n\n");
    }
    setLogText(log);
    setShowLog(true);
  };

  const openPasswordModal = () => {
    setPassword("");
    setModalError("");
    setShowPasswordModal(true);
    setTimeout(() => document.getElementById("log-pw-input")?.focus(), 50);
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim()) return;
    setModalLoading(true);
    setModalError("");
    try {
      const res = await fetch(`/api/log/view?key=${encodeURIComponent(password)}&tailLines=1`);
      if (res.ok) {
        await fetchServerLog(password);
        setStoredKey(password);
        setShowServerLog(true);
        setShowPasswordModal(false);
        setPassword("");
        setSearchTerm("");
        setSearchMatches([]);
        setSearchMeta({ totalMatches: 0, truncated: false });
        setActiveMatchIndex(0);
      } else {
        setModalError("Incorrect password");
      }
    } catch (err) {
      setModalError("Server unavailable");
    }
    setModalLoading(false);
  };

  const handleClearLog = async () => {
    if (!window.confirm("Clear the activity log?")) return;
    try {
      const res = await fetch(`/api/log/clear?key=${encodeURIComponent(storedKey)}`);
      if (res.ok) {
        setServerEntries(["(log cleared)"]);
        setSearchMatches([]);
        setSearchTerm("");
        setSearchMeta({ totalMatches: 0, truncated: false });
      }
    } catch {}
  };

  const activeEntries = searchTerm.trim() ? searchMatches : serverEntries;
  const hasSearchResults = searchTerm.trim() && searchMatches.length > 0;
  const activeDisplayTotal = hasSearchResults ? searchMatches.length : 0;

  const goToPreviousMatch = () => {
    if (!hasSearchResults) return;
    setActiveMatchIndex((prev) => (prev - 1 + searchMatches.length) % searchMatches.length);
  };

  const goToNextMatch = () => {
    if (!hasSearchResults) return;
    setActiveMatchIndex((prev) => (prev + 1) % searchMatches.length);
  };

  const renderLogEntry = (line, index) => {
    const isActive = hasSearchResults && index === activeMatchIndex;
    const chunks = line
      .split("|")
      .map((part) => part.trim())
      .filter(Boolean);

    return (
      <div
        key={`${line}-${index}`}
        ref={(node) => {
          logLineRefs.current[index] = node;
        }}
        className={`server-log-line ${isActive ? "active" : ""}`}
      >
        {chunks.map((chunk, chunkIndex) => {
          const separator = chunkIndex > 0 ? <span className="log-separator"> | </span> : null;
          const colonIndex = chunk.indexOf(":");

          if (colonIndex === -1) {
            return (
              <React.Fragment key={`${chunk}-${chunkIndex}`}>
                {separator}
                <span className="log-value tone-default">{highlightText(chunk, searchTerm)}</span>
              </React.Fragment>
            );
          }

          const key = chunk.slice(0, colonIndex).trim();
          const value = chunk.slice(colonIndex + 1).trim();
          const toneClass = getToneByKey(key);

          return (
            <React.Fragment key={`${key}-${chunkIndex}`}>
              {separator}
              <span className={`log-key ${toneClass}`}>{key}:</span>
              <span className={`log-value ${toneClass}`}> {highlightText(value, searchTerm)}</span>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // Modal overlay styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };
  const modalStyle = {
    background: "#fff",
    borderRadius: 8,
    padding: "24px 28px",
    width: 320,
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  };

  return (
    <footer className="app-footer">
      <div className="footer-content">
        <div className="developer-credit">
          <span className="designed-text">Designed and Developed by</span>
          <span className="developer-name">TANVIR</span>
        </div>
        <div className="copyright">
          © {year} Tanvir Hossain. All{" "}
          <span className="hidden-rights" onClick={handleRightsClick}>
            rights
          </span>{" "}
          reserved.
          <span style={{ marginLeft: "20px", fontSize: "12px" }}>
            <button
              onClick={openPasswordModal}
              style={{
                background: "none",
                border: "1px solid #ccc",
                padding: "2px 8px",
                fontSize: "11px",
                cursor: "pointer",
                borderRadius: "3px",
                color: "#666",
              }}
            >
              Server Task
            </button>
          </span>
        </div>

        {/* Password Modal */}
        {showPasswordModal && (
          <div style={overlayStyle} onClick={() => setShowPasswordModal(false)}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: "#333" }}>
                Authentication Required
              </div>
              <input
                id="log-pw-input"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setModalError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && handlePasswordSubmit()}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  fontSize: 13,
                  border: "1px solid #ddd",
                  borderRadius: 5,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {modalError && (
                <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>{modalError}</div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  style={{
                    padding: "6px 16px",
                    fontSize: 12,
                    border: "1px solid #ddd",
                    borderRadius: 5,
                    background: "#f5f5f5",
                    cursor: "pointer",
                    color: "#555",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordSubmit}
                  disabled={modalLoading}
                  style={{
                    padding: "6px 16px",
                    fontSize: 12,
                    border: "none",
                    borderRadius: 5,
                    background: "#1a73e8",
                    color: "#fff",
                    cursor: "pointer",
                    opacity: modalLoading ? 0.6 : 1,
                  }}
                >
                  {modalLoading ? "..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Server Log Viewer */}
        {showServerLog && (
          <div className="server-log-card">
            <div className="server-log-search-row">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search logs"
                className="server-log-search-input"
              />
              <div className="server-log-search-nav">
                <button
                  onClick={goToPreviousMatch}
                  disabled={!hasSearchResults}
                  className="tiny-nav-btn"
                  aria-label="Previous match"
                >
                  &lt;
                </button>
                <button
                  onClick={goToNextMatch}
                  disabled={!hasSearchResults}
                  className="tiny-nav-btn"
                  aria-label="Next match"
                >
                  &gt;
                </button>
                <span className="search-counter">
                  {hasSearchResults ? `${activeMatchIndex + 1}/${activeDisplayTotal}` : "0/0"}
                </span>
              </div>
            </div>

            <div className="server-log-meta-row">
              <span>
                {searchTerm.trim()
                  ? `${searchMeta.totalMatches} match${searchMeta.totalMatches === 1 ? "" : "es"}`
                  : `Showing latest ${serverEntries.length} entries`}
                {searchMeta.truncated ? " (partial list)" : ""}
              </span>
              {searchLoading && <span>Searching...</span>}
            </div>

            <div className="server-log-viewer">
              {activeEntries.length > 0 ? (
                activeEntries.map((line, index) => renderLogEntry(line, index))
              ) : (
                <div className="server-log-empty">No matching entries.</div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
              <button
                onClick={handleClearLog}
                style={{
                  fontSize: 12,
                  color: "#c00",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Clear Log
              </button>
              <button
                onClick={() => {
                  setShowServerLog(false);
                  setStoredKey("");
                  setSearchTerm("");
                  setSearchMatches([]);
                  setSearchMeta({ totalMatches: 0, truncated: false });
                  setActiveMatchIndex(0);
                }}
                style={{ fontSize: 12, background: "none", border: "none", cursor: "pointer" }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Local Log (rights click) */}
        {showLog && (
          <div style={{ marginTop: 16, width: "100%", maxWidth: 600 }}>
            <textarea
              value={logText}
              onChange={handleLogChange}
              style={{
                width: "100%",
                height: 150,
                resize: "vertical",
                fontSize: 12,
                fontFamily: "monospace",
                overflowY: "scroll",
                padding: 10,
                lineHeight: 1.5,
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
              <span style={{ fontSize: 11, color: "#777", fontStyle: "italic" }}>
                You can edit or delete logs. Changes save automatically on close.
              </span>
              <button style={{ fontSize: 12 }} onClick={handleCloseLog}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
